import { useReducer, useEffect } from 'react';
import { AuthUser, LoginCredentials } from '@/types/auth';
import { AuthService } from '@/services/authService';

interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  error: string | null;
}

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: AuthUser | null }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'LOGIN_SUCCESS'; payload: AuthUser }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' };

const initialState: AuthState = {
  user: null,
  isLoading: true,
  error: null,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_USER':
      return { ...state, user: action.payload, isLoading: false, error: null };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'LOGIN_SUCCESS':
      return { ...state, user: action.payload, isLoading: false, error: null };
    case 'LOGOUT':
      return { ...state, user: null, error: null };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
}

export function useAuthReducer() {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const authService = AuthService.getInstance();

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        const currentUser = authService.getCurrentUser();
        dispatch({ type: 'SET_USER', payload: currentUser });
      } catch (error) {
        console.error('Error initializing auth:', error);
        dispatch({ type: 'SET_USER', payload: null });
      }
    };

    initializeAuth();
  }, [authService]);

  // Auto-logout when session expires
  useEffect(() => {
    if (!state.user) return;

    const checkSession = () => {
      const sessionInfo = authService.getSessionInfo();
      if (!sessionInfo || !sessionInfo.isValid) {
        handleLogout();
      }
    };

    // Check session every minute
    const interval = setInterval(checkSession, 60 * 1000);
    
    return () => clearInterval(interval);
  }, [state.user, authService]);

  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });
    
    try {
      const response = await authService.login(credentials);
      
      if (response.success && response.data) {
        dispatch({ type: 'LOGIN_SUCCESS', payload: response.data });
        return true;
      } else {
        dispatch({ type: 'SET_ERROR', payload: response.error || 'Login failed' });
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Network error occurred' });
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    await handleLogout();
  };

  const handleLogout = async (): Promise<void> => {
    try {
      await authService.logout();
    } catch (error) {
      console.warn('Logout error:', error);
    } finally {
      dispatch({ type: 'LOGOUT' });
    }
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const isAuthenticated = state.user !== null && state.user.isAuthenticated;

  return {
    ...state,
    login,
    logout,
    clearError,
    isAuthenticated,
  };
}